/**
 * Temporary layers — a person's own scratch results on someone else's map.
 *
 * A buffer, and later any other analysis output, lands in the TOC as a real
 * layer: it draws, it styles, it labels, it identifies, and it stays until it
 * is removed. What it never does is join the MODULE.
 *
 * That distinction is the whole point, and it is enforced in two places rather
 * than one, because either alone would be a privacy bug:
 *
 *  1. `draftModule()` strips `temporary` layers before saving, so a buffer
 *     someone drew can never be written into the shared application — an admin
 *     hitting Save must not publish another person's scratch work to everyone.
 *  2. Storage here is keyed by USER AND MODULE together, so two people looking
 *     at the same map keep separate scratch layers and neither sees the other's.
 *
 * Signed out, nothing is written at all: an anonymous key would be shared by
 * every anonymous session on the machine, which is exactly the leak this
 * module exists to prevent.
 */

import type { Layer } from '../types';
import { readJson, writeJson, removeKey } from './storage';

const PREFIX = 'blueview-temp-layers';

/** One bucket per person per map. */
function keyFor(userId: string | undefined, moduleId: string): string | null {
  if (!userId || !moduleId) return null;
  return `${PREFIX}:${userId}:${moduleId}`;
}

export const temporaryLayerService = {
  /** This person's scratch layers for this map. Never another person's. */
  load(userId: string | undefined, moduleId: string): Layer[] {
    const key = keyFor(userId, moduleId);
    if (!key) return [];
    const stored = readJson<Layer[] | null>(key, null);
    if (!Array.isArray(stored)) return [];
    // The flag is re-applied on read: a layer that lost it would be saved into
    // the module the next time someone pressed Save.
    return stored.map((layer) => ({ ...layer, temporary: true }));
  },

  save(userId: string | undefined, moduleId: string, layers: Layer[]): void {
    const key = keyFor(userId, moduleId);
    if (!key) return;
    const temporary = layers.filter((layer) => layer.temporary);
    if (temporary.length === 0) {
      removeKey(key);
      return;
    }
    writeJson(key, temporary);
  },
};

/** Everything that belongs to the saved application — scratch work removed. */
export function withoutTemporary(layers: Layer[]): Layer[] {
  return layers.filter((layer) => !layer.temporary);
}
